import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  FormGroup,
  Label,
  InputGroup,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { BeatLoader } from 'react-spinners';
import {
  getLossPreventionSettings,
  saveLossPreventionSettings,
} from '../../actions/lossPreventionActions';

const LossPreventionSettingsPage = () => {
  const dispatch = useDispatch();
  const { data, isLoading, isSaving } = useSelector(
    (state) => state.lossPreventionSettings,
  );

  const [form, setForm] = useState({
    voidTransactions: '',
    voidLines: '',
    noSales: '',
    priceOverrides: '',
    refunds: '',
    wastage: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(getLossPreventionSettings());
  }, [dispatch]);

  useEffect(() => {
    if (data && !isSaving) {
      setForm({
        voidTransactions: data.voidTransactions,
        voidLines: data.voidLines,
        noSales: data.noSales,
        priceOverrides: data.priceOverrides,
        refunds: data.refunds,
        wastage: data.wastage,
      });
    }
  }, [data, isSaving]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'voidTransactions' ? parseFloat(value) : value,
    }));
  };

  const greaterThan = (value, length, label) => {
    if (value === undefined || value <= length) {
      return `${label} must be greater than ${length}`;
    }
  };

  const validate = (model) => {
    const errors = {};
    const addError = (message, fieldName) => {
      if (message !== undefined) {
        errors[fieldName] = message;
      }
    };

    addError(
      greaterThan(model.voidTransactions, 0, 'Void Transactions'),
      'voidTransactions',
    );
    addError(greaterThan(model.voidLines, 0, 'Void Lines'), 'voidLines');
    addError(greaterThan(model.noSales, 0, 'No Sales'), 'noSales');
    addError(
      greaterThan(model.priceOverrides, 0, 'Price Overrides'),
      'priceOverrides',
    );
    addError(greaterThan(model.refunds, 0, 'Refunds'), 'refunds');
    addError(greaterThan(model.wastage, 0, 'Wastage'), 'wastage');

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  const handleSave = (e) => {
    e.preventDefault();
    const result = validate(form);

    if (result.isValid) {
      dispatch(saveLossPreventionSettings(form, form));
    } else {
      setErrors(result.errors);
    }
  };

  if (isLoading) {
    return <BeatLoader color={'#004C5F'} />;
  }

  const step = 0.01;

  return (
    <div>
      <h4>Loss Prevention Settings</h4>
      <Row>
        <Col md={4} sm={12}>
          <Card>
            <CardHeader>
              <h6 className='mt-2'>Exceptions</h6>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label for='voidTransactions'>Void Transactions</Label>
                <InputGroup>
                  <div className='input-group-append'>
                    <span className='input-group-text'>$</span>
                  </div>
                  <Input
                    className='form-control'
                    type='number'
                    min={0}
                    step={step}
                    onChange={handleChange}
                    name='voidTransactions'
                    id='voidTransactions'
                    value={form.voidTransactions}
                  />
                </InputGroup>
                {errors.voidTransactions && (
                  <div className='validation-error'>
                    {errors.voidTransactions}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for='voidLines'>Void Lines</Label>
                <InputGroup>
                  <div className='input-group-append'>
                    <span className='input-group-text'>$</span>
                  </div>
                  <Input
                    className='form-control'
                    type='number'
                    min={0}
                    step={step}
                    onChange={handleChange}
                    name='voidLines'
                    id='voidLines'
                    value={form.voidLines}
                  />
                </InputGroup>
                {errors.voidLines && (
                  <div className='validation-error'>{errors.voidLines}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for='noSales'>No Sales</Label>
                <InputGroup>
                  <div className='input-group-append'>
                    <span className='input-group-text'>$</span>
                  </div>
                  <Input
                    className='form-control'
                    type='number'
                    min={0}
                    step={step}
                    onChange={handleChange}
                    name='noSales'
                    id='noSales'
                    value={form.noSales}
                  />
                </InputGroup>
                {errors.noSales && (
                  <div className='validation-error'>{errors.noSales}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for='priceOverrides'>Price Overrides</Label>
                <InputGroup>
                  <div className='input-group-append'>
                    <span className='input-group-text'>$</span>
                  </div>
                  <Input
                    className='form-control'
                    type='number'
                    min={0}
                    step={step}
                    onChange={handleChange}
                    name='priceOverrides'
                    id='priceOverrides'
                    value={form.priceOverrides}
                  />
                </InputGroup>
                {errors.priceOverrides && (
                  <div className='validation-error'>
                    {errors.priceOverrides}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for='refunds'>Refunds</Label>
                <InputGroup>
                  <div className='input-group-append'>
                    <span className='input-group-text'>$</span>
                  </div>
                  <Input
                    className='form-control'
                    type='number'
                    min={0}
                    step={step}
                    onChange={handleChange}
                    name='refunds'
                    id='refunds'
                    value={form.refunds}
                  />
                </InputGroup>
                {errors.refunds && (
                  <div className='validation-error'>{errors.refunds}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for='wastage'>Wastage</Label>
                <InputGroup>
                  <div className='input-group-append'>
                    <span className='input-group-text'>$</span>
                  </div>
                  <Input
                    className='form-control'
                    type='number'
                    min={0}
                    step={step}
                    onChange={handleChange}
                    name='wastage'
                    id='wastage'
                    value={form.wastage}
                  />
                </InputGroup>
                {errors.wastage && (
                  <div className='validation-error'>{errors.wastage}</div>
                )}
              </FormGroup>
              <FormGroup className='buttons-panel'>
                <Button color='primary' onClick={handleSave}>
                  <FontAwesomeIcon icon={faSave} /> Save
                </Button>
              </FormGroup>
            </CardBody>
          </Card>
          {isSaving && <BeatLoader color={'#004C5F'} />}
        </Col>
      </Row>
    </div>
  );
};

export default LossPreventionSettingsPage;
